import React, { useState } from "react";
import PriorityBadge from "./PriorityBadge";
import StatusBadge from "./StatusBadge";
import LeadStatusBadge from "./LeadStatusBadge";
import { api } from "../api";

const PIPELINE_STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "replied", label: "Replied" },
  { value: "meeting_booked", label: "Meeting Booked" },
  { value: "sent_pricing", label: "Sent Pricing" },
  { value: "sent_contract", label: "Sent Contract" },
  { value: "not_interested", label: "Not Interested" },
  { value: "closed", label: "Closed" },
];

function formatFunding(amount) {
  if (!amount) return "N/A";
  if (amount >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1)}B`;
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}K`;
  return `$${amount}`;
}

export default function LeadDetail({ lead, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const fc = lead.form_context;

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    setSaving(true);
    try {
      const updated = await api(`/leads/${lead.id}/pipeline-status`, {
        method: "PATCH",
        body: JSON.stringify({ lead_status: newStatus }),
      });
      if (onUpdate) onUpdate(updated);
    } catch (err) {
      console.error("Failed to update pipeline status:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="lead-detail">
      <div className="detail-header">
        <h3>
          {lead.first_name} {lead.last_name}
        </h3>
        <div className="badges">
          <PriorityBadge priority={lead.priority} />
          <StatusBadge status={lead.status} />
          <LeadStatusBadge leadStatus={lead.lead_status || "new"} />
        </div>
      </div>

      {lead.followup_count > 0 && lead.status === "pending" && (
        <div className="followup-indicator">
          🔄 Follow-up #{lead.followup_count} of 3 — awaiting approval
        </div>
      )}
      {lead.followup_count > 0 && lead.status === "sent" && (
        <div className="followup-indicator followup-sent">
          ✓ {lead.followup_count} follow-up{lead.followup_count > 1 ? "s" : ""} sent
        </div>
      )}

      <div className="pipeline-status-row">
        <label>Pipeline Status</label>
        <select
          className="pipeline-select"
          value={lead.lead_status || "new"}
          onChange={handleStatusChange}
          disabled={saving}
        >
          {PIPELINE_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <div className="detail-grid">
        <div><label>Email</label><span>{lead.email}</span></div>
        <div><label>Job Title</label><span>{lead.job_title || "N/A"}</span></div>
        <div><label>Company</label><span>{lead.company || "N/A"}</span></div>
        <div><label>Industry</label><span>{lead.industry || "N/A"}</span></div>
        <div><label>Employees</label><span>{lead.company_size || "N/A"}</span></div>
        <div><label>Country</label><span>{lead.country || "N/A"}</span></div>
        <div><label>Founded</label><span>{lead.founded_year || "N/A"}</span></div>
        <div><label>Funding</label><span>{formatFunding(lead.total_funding)}</span></div>
        {lead.linkedin_url && (
          <div><label>LinkedIn</label><a href={lead.linkedin_url} target="_blank" rel="noreferrer">Profile</a></div>
        )}
        {lead.company_website && (
          <div><label>Website</label><a href={lead.company_website} target="_blank" rel="noreferrer">{lead.company_website}</a></div>
        )}
      </div>

      {lead.keywords?.length > 0 && (
        <div className="detail-keywords">
          <label>Keywords</label>
          <div className="keyword-list">
            {lead.keywords.map((kw, i) => (
              <span key={i} className="keyword">{kw}</span>
            ))}
          </div>
        </div>
      )}

      {fc && (
        <div className="detail-form-context">
          <h4>Form Submission</h4>
          <div className="detail-grid">
            {fc.goals?.length > 0 && (
              <div><label>Goals</label><span>{fc.goals.join(", ")}</span></div>
            )}
            {fc.budget && <div><label>Budget</label><span>{fc.budget}</span></div>}
            {fc.runningLinkedinAds && (
              <div><label>Running LinkedIn Ads</label><span>{fc.runningLinkedinAds}</span></div>
            )}
            {fc.about && <div><label>About</label><span>{fc.about}</span></div>}
            {fc.referral && <div><label>Referral</label><span>{fc.referral}</span></div>}
            {fc.hearAboutUs && (
              <div><label>How they heard</label><span>{fc.hearAboutUs}</span></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
